// --------------------------------------------------------------------------
// Components of satellite database page including their logical connections.
// --------------------------------------------------------------------------

import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from 'react-router-dom';
import { Box, Flex, Image, Spacer, Button, IconButton, Drawer, DrawerOverlay,
  DrawerCloseButton, DrawerHeader, DrawerContent, DrawerBody, DrawerFooter,
  useDisclosure, Breadcrumb, BreadcrumbItem, BreadcrumbLink, Avatar, Text,
  Heading, useTheme, Table, Thead, Tbody,Th, Tr, Td, Spinner, Input} 
  from "@chakra-ui/react";
import { Menu, Logout } from "@mui/icons-material";
import { useAuth } from '../context/security';
import logo_emi from "./assets/emi_inverted.png";
import "./styles.css";


// --- BACKGROUND ---
function Background() {
  return(
    <Box className="background"></Box>
  );
};

const Copyright = () => {
  const theme = useTheme();
  
  return (
    <Box
      position="fixed"
      bottom="10px"
      right="10px"
      zIndex={0}>
      <Text
        color="black"
        fontSize={theme.fontSize.info}>
      </Text>
    </Box>
  );
};

// --- HEADER ---
function Header() {

  // 1. State, hooks, event handling and helper functions
  const theme = useTheme();
  const lastRef = React.useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure()
  const { clearAuthToken } = useAuth();

  const handleLogout = () => {
    clearAuthToken(); 
    navigate('/');
  };

  // 2. JSX
  return(
    <Flex px={4} alignItems="center" className="header">
      <Flex alignItems="center" height="100%" gap={12}>
        <Image
          src={logo_emi} alt="Logo EMI"
          width="auto" height="70%"/>
        <Breadcrumb>
          <BreadcrumbItem color="white" fontFamily="label">
            <BreadcrumbLink as={Link} to="/main">Main</BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem color="white" fontFamily="label">
            <BreadcrumbLink as={Link} to="/satellite-db">Satellite Database</BreadcrumbLink>
          </BreadcrumbItem>
        </Breadcrumb>
      </Flex>
      <Spacer />
      <IconButton
        variant="plain" aria-label="Menu"
        size="lg" color="white"
        icon={<Menu />}
        ref={lastRef}
        onClick={onOpen}/>
      <Drawer
        isOpen={isOpen}
        placement='right'
        onClose={onClose}
        finalFocusRef={lastRef}>
        <DrawerOverlay />
        <DrawerContent bg={theme.colors.main.drawer} color="white">
          <DrawerCloseButton />
          <DrawerHeader>
            <Flex flex='1' gap='4' alignItems='center' flexWrap='wrap'>
              <Avatar
                name={sessionStorage.getItem("username") || "Unknown"}
                bg={theme.colors.main.highlightLight}
                color={theme.colors.main.highlightDark}/>
              <Box>
                <Heading fontSize={theme.fontSize.heading}>
                  {sessionStorage.getItem("username") || "Unknown"}
                </Heading>
                <Text fontSize={theme.fontSize.info}>
                  Premium Account
                </Text>
              </Box> 
            </Flex>
          </DrawerHeader>
          <DrawerBody>
          </DrawerBody>
          <DrawerFooter>
            <Button
              leftIcon={<Logout fontSize={theme.fontSize.iconLeft} />}
              onClick={handleLogout}>
              Log out
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </Flex>
  );
};

// --- BODY ---
const Body = () => {
  const theme = useTheme();
  const { token } = useAuth();
  const [satellites, setSatellites] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Filter & Sort state
  const [filterName, setFilterName] = useState("");
  const [filterProvider, setFilterProvider] = useState("");
  const [sortKey, setSortKey] = useState<"provider" | "platform_name" | "sensor">(
    "provider"
  );
  const [sortAsc, setSortAsc] = useState(false);

  useEffect(() => {
  const fetchSatellites = async () => {
    try {
      const response = await fetch("http://localhost:8000/api/db/sat", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      console.log("Status:", response.status);

      const text = await response.text();
      console.log("Raw answer:", text);

      try {
        const data = JSON.parse(text);
        console.log("Parsed JSON:", data);

        // Falls es ein verschachteltes Objekt ist:
        if (data.eo_platforms) {
          console.log("Keys in eo_platforms:", Object.keys(data.eo_platforms));
        }

        const satArray = Object.entries(data.eo_platforms).map(
          ([id, details]: any) => ({
            id,
            ...details,
          })
        );
        setSatellites(satArray);
      } catch (e) {
        console.error("Invalid JSON", e);
      }
    } catch (error) {
      console.error("Error while loading:", error);
    } finally {
      setLoading(false);
    }
  };

  if (token) {
    fetchSatellites();
  } else {
    setLoading(false);
  }
}, [token]);

  // Filter & sort
  const displayedSatellites = useMemo(() => {
    let filtered = satellites.filter(
      (sat) =>
        sat.platform_name.toLowerCase().includes(filterName.toLowerCase()) &&
        sat.provider.toLowerCase().includes(filterProvider.toLowerCase())
    );

    filtered.sort((a, b) => {
      let valA: string;
      let valB: string;

      if (sortKey === "sensor") {
        valA = a.sensor.join(", ").toLowerCase();
        valB = b.sensor.join(", ").toLowerCase();
      } else {
        valA = a[sortKey].toLowerCase();
        valB = b[sortKey].toLowerCase();
      }

      if (valA < valB) return sortAsc ? -1 : 1;
      if (valA > valB) return sortAsc ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [satellites, filterName, filterProvider, sortKey, sortAsc]);


  if (loading) {
    return (
      <Box className="body-layout-column" bg={theme.colors.main.box}>
        <Flex justify="center" align="center" direction="column">
          <Spinner size="xl" />
          <Text mt={4}>Lade Satelliten-Daten...</Text>
        </Flex>
      </Box>
    );
  }
  
  const toggleSort = (key: typeof sortKey) => {
    if (sortKey === key) setSortAsc(!sortAsc);
    else {
      setSortKey(key);
      setSortAsc(true);
    }
  };

   return (
    <Box bg={theme.colors.main.box} className="body-layout-column">
      <Heading fontSize={theme.fontSize.heading} mb={6}>
        Satellite Database
      </Heading>

      {/* Filter */}
      <Flex gap={4} mb={4} wrap="wrap">
        <Input
          placeholder="Filter by Name"
          value={filterName}
          onChange={(e) => setFilterName(e.target.value)}
          maxW="200px"
          bg={theme.colors.main.drawer}
          color={theme.colors.main.white}
        />
        <Input
          placeholder="Filter by Provider"
          value={filterProvider}
          onChange={(e) => setFilterProvider(e.target.value)}
          maxW="200px"
          bg={theme.colors.main.drawer}
          color={theme.colors.main.white}
        />
      </Flex>

      <Box overflowX="auto">
        <Table variant="simple" size="md">
          <Thead bg={theme.colors.main.drawer}>
            <Tr>
              <Th
                color={theme.colors.main.white}
                fontFamily={theme.fonts.heading}
                onClick={() => toggleSort("provider")}
                cursor="pointer"
              >
                Provider{" "}
                {sortKey === "provider" && (sortAsc ? "▲" : "▼")}
                
              </Th>
              <Th
                color={theme.colors.main.white}
                fontFamily={theme.fonts.heading}
                onClick={() => toggleSort("platform_name")}
                cursor="pointer"
              >
                Platform Name{" "}
                {sortKey === "platform_name" && (sortAsc ? "▲" : "▼")}
              </Th>
              <Th
                color={theme.colors.main.white}
                fontFamily={theme.fonts.heading}
                onClick={() => toggleSort("sensor")}
                cursor="pointer"
              >
                Sensors{" "}
                {sortKey === "sensor" && (sortAsc ? "▲" : "▼")}
                 
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {displayedSatellites.map((sat, idx) => (
              <Tr key={idx} _hover={{ bg: theme.colors.main.box, cursor: "default" }}>
                <Td color={theme.colors.main.results} fontFamily={theme.fonts.body}>
                  {sat.platform_name}
                </Td>
                <Td color={theme.colors.main.results} fontFamily={theme.fonts.body}>
                  {sat.provider}
                </Td>
                <Td color={theme.colors.main.results} fontFamily={theme.fonts.body}>
                  {sat.sensor.join(", ")}
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
};

const SatDBPage = () => {
  return(
    <Box className="layout-column">
      <Header />
      <Body />
      <Background />
      <Copyright />
    </Box>
  );
};

export default SatDBPage;